import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Plus, Edit, Trash2, ClipboardList, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ChecklistManagement() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [selectedKitchen, setSelectedKitchen] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [form, setForm] = useState({
    outlet_id: "",
    name: "",
    items: [{ item_text: "", is_required: true }]
  });

  useEffect(() => {
    fetchKitchens();
  }, []);

  useEffect(() => {
    if (selectedKitchen) {
      fetchOutlets(selectedKitchen);
    }
  }, [selectedKitchen]);

  const fetchKitchens = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/kitchens`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setKitchens(data);
        if (data.length > 0) {
          setSelectedKitchen(data[0].kitchen_id);
        }
      }
    } catch (error) {
      console.error("Error fetching kitchens:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutlets = async (kitchenId) => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/outlets?kitchen_id=${kitchenId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setOutlets(data);
        // Fetch templates for all outlets
        fetchTemplates(data.map(o => o.outlet_id));
      }
    } catch (error) {
      console.error("Error fetching outlets:", error);
    }
  };

  const fetchTemplates = async (outletIds) => {
    try {
      const allTemplates = [];
      for (const outletId of outletIds) {
        const res = await fetch(`${API_URL}/api/ops-extended/checklist-templates?outlet_id=${outletId}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          allTemplates.push(...data);
        }
      }
      setTemplates(allTemplates);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const handleAddItem = () => {
    setForm({
      ...form,
      items: [...form.items, { item_text: "", is_required: true }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setForm({ ...form, items: newItems });
  };

  const handleSaveTemplate = async () => {
    if (!form.outlet_id || !form.name || form.items.filter(i => i.item_text).length === 0) {
      toast({ title: "Error", description: "Please fill all required fields", variant: "destructive" });
      return;
    }

    const validItems = form.items.filter(i => i.item_text.trim());

    try {
      const url = editingId 
        ? `${API_URL}/api/ops-extended/checklist-templates/${editingId}`
        : `${API_URL}/api/ops-extended/checklist-templates`;
      
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, items: validItems })
      });
      
      if (res.ok) {
        toast({ title: `Checklist ${editingId ? "updated" : "created"} successfully` });
        setDialogOpen(false);
        setForm({ outlet_id: "", name: "", items: [{ item_text: "", is_required: true }] });
        setEditingId(null);
        fetchOutlets(selectedKitchen);
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save checklist", variant: "destructive" });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this checklist?")) return;
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/checklist-templates/${templateId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast({ title: "Checklist deleted successfully" });
        fetchOutlets(selectedKitchen);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete checklist", variant: "destructive" });
    }
  };

  const handleEditTemplate = (template) => {
    setForm({
      outlet_id: template.outlet_id,
      name: template.name,
      items: template.items
    });
    setEditingId(template.template_id);
    setDialogOpen(true);
  };

  const getOutletName = (outletId) => {
    const outlet = outlets.find(o => o.outlet_id === outletId);
    return outlet ? outlet.name : "Unknown";
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="checklist-management">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Checklist Management
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingId(null); setForm({ outlet_id: "", name: "", items: [{ item_text: "", is_required: true }] }); }}>
                <Plus className="h-4 w-4 mr-2" /> Create Checklist
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Checklist" : "Create New Checklist"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Kitchen</Label>
                  <Select value={selectedKitchen} onValueChange={setSelectedKitchen}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select kitchen" />
                    </SelectTrigger>
                    <SelectContent>
                      {kitchens.map((k) => (
                        <SelectItem key={k.kitchen_id} value={k.kitchen_id}>{k.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Outlet *</Label>
                  <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select outlet" />
                    </SelectTrigger>
                    <SelectContent>
                      {outlets.map((o) => (
                        <SelectItem key={o.outlet_id} value={o.outlet_id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Checklist Name *</Label>
                  <Input 
                    value={form.name} 
                    onChange={(e) => setForm({ ...form, name: e.target.value })} 
                    placeholder="e.g., Daily Opening Checklist"
                  />
                </div>
                <div>
                  <Label>Checklist Items</Label>
                  <div className="space-y-2 mt-2">
                    {form.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input 
                          value={item.item_text}
                          onChange={(e) => handleItemChange(index, "item_text", e.target.value)}
                          placeholder={`Item ${index + 1}`}
                          className="flex-1"
                        />
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={item.is_required}
                            onCheckedChange={(v) => handleItemChange(index, "is_required", v)}
                          />
                          <span className="text-sm">Required</span>
                        </div>
                        {form.items.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(index)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" onClick={handleAddItem} className="mt-2">
                    <Plus className="h-4 w-4 mr-2" /> Add Item
                  </Button>
                </div>
                <Button onClick={handleSaveTemplate} className="w-full">Save Checklist</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {/* Kitchen Selector */}
          <div className="flex flex-wrap gap-2 mb-6">
            {kitchens.map((kitchen) => (
              <Button
                key={kitchen.kitchen_id}
                variant={selectedKitchen === kitchen.kitchen_id ? "default" : "outline"}
                onClick={() => setSelectedKitchen(kitchen.kitchen_id)}
              >
                {kitchen.name}
              </Button>
            ))}
          </div>

          {/* Templates by Outlet */}
          {outlets.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No outlets found for this kitchen. Add outlets first.
            </p>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {outlets.map((outlet) => {
                const outletTemplates = templates.filter(t => t.outlet_id === outlet.outlet_id);
                return (
                  <AccordionItem key={outlet.outlet_id} value={outlet.outlet_id} className="border rounded-lg">
                    <AccordionTrigger className="px-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <span>{outlet.name}</span>
                        <span className="text-sm text-muted-foreground">{outletTemplates.length} checklists</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      {outletTemplates.length === 0 ? (
                        <p className="text-muted-foreground text-sm">No checklists for this outlet</p>
                      ) : (
                        <div className="space-y-3">
                          {outletTemplates.map((template) => (
                            <div key={template.template_id} className="border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium">{template.name}</h4>
                                <div className="flex gap-2">
                                  <Button variant="outline" size="sm" onClick={() => handleEditTemplate(template)}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button variant="destructive" size="sm" onClick={() => handleDeleteTemplate(template.template_id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                                {template.items.map((item, idx) => (
                                  <li key={idx}>
                                    {item.item_text}
                                    {item.is_required && <span className="text-red-500 ml-1">*</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
